import { isEmpty } from 'lodash-es';
import {
  CalendarTypes,
  type CalendarEvent,
  type CalendarObject,
  type RecordEntityId,
  type StationEvents,
  isApiError,
} from '@centric-os/types';
import { defineStore, storeToRefs, type StateTree } from 'pinia';
import { useCalendar } from '../../../composables';
import { useStationStore } from '../use-station-store';
import { useSiteStore } from '@centric-os/stores/sites/use-site-store';
import { CalendarRevisionConflictError } from './calendar-revision-conflict';

interface State extends StateTree {
  allStationsCalendarEvents: Record<string, CalendarObject>;
  structuredStationCalendarEvents: StationEvents;
  currentEvent: CalendarEvent;
}

/** App-wide calendar cache (default Pinia store id `calendar`). Call `useCalendarStore()` with no args. */
export function useCalendarStore(storeId = 'calendar') {
  const store = defineStore(storeId, {
    state: (): State => ({
      allStationsCalendarEvents: {},
      structuredStationCalendarEvents: null,
      currentEvent: null,
    }),
    actions: {
      async getAllLocationCalendarEvents(ids: RecordEntityId[], refreshValues = false) {
        const { structureCalendarEvents } = useCalendar();
        const allCalendarsMap: Record<string, CalendarObject> = {};
        const structuredEventsMap: StationEvents = {};
        await Promise.all(
          ids.map(async (id) => {
            if (!this.allStationsCalendarEvents?.[id] || refreshValues) {
              const calendar = await this.getCalendar(id);
              allCalendarsMap[calendar.id] = calendar;
              structuredEventsMap[calendar.id] = structureCalendarEvents(calendar);
              return;
            }
            structuredEventsMap[id] = structureCalendarEvents(this.allStationsCalendarEvents[id]);
          }),
        );
        this.allStationsCalendarEvents = { ...this.allStationsCalendarEvents, ...allCalendarsMap };
        const previousStructured = this.structuredStationCalendarEvents ?? {};
        this.structuredStationCalendarEvents = {
          ...previousStructured,
          ...structuredEventsMap,
        };
      },
      async getCalendar(id: RecordEntityId): Promise<CalendarObject> {
        const response = await this.serviceApi.get_calendar(String(id), {
          query: { nocache: true },
        });

        return response as CalendarObject;
      },
      applyPutCalendarResponse(stationId: string, calendar: CalendarObject) {
        const { structureCalendarEvents } = useCalendar();
        this.allStationsCalendarEvents = {
          ...this.allStationsCalendarEvents,
          [calendar.id]: calendar,
        };
        const structured = structureCalendarEvents(calendar);
        if (!this.structuredStationCalendarEvents) {
          this.structuredStationCalendarEvents = { [stationId]: structured };
        } else {
          this.structuredStationCalendarEvents = {
            ...this.structuredStationCalendarEvents,
            [stationId]: structured,
          };
        }
      },
      async saveCalendarEvents(
        stationId: string,
        updateCurrentEvent?: boolean,
        deleteCurrentEvent?: boolean,
      ) {
        const eventsCopy = [];
        let allEvents: CalendarEvent[] = [];
        if (this.structuredStationCalendarEvents?.[stationId]) {
          const { menuEvents, businessEvents, closureEvents, deliveryEvents } =
            this.structuredStationCalendarEvents[stationId];
          allEvents = [...businessEvents, ...closureEvents, ...deliveryEvents];
          Object.values(menuEvents).forEach((mEvent) => {
            allEvents = [...allEvents, ...mEvent];
          });
        }
        if (updateCurrentEvent && !deleteCurrentEvent) eventsCopy.push(this.currentEvent);
        allEvents.forEach((event) => {
          const { schedule, data } = event;

          if (deleteCurrentEvent && data?.id === this.currentEvent?.data?.id) return; // Delete
          if (!updateCurrentEvent && !data.id) return;
          if (updateCurrentEvent && !deleteCurrentEvent && data?.id === this.currentEvent?.data?.id)
            return; // Prevent duplicates when updating
          if (!schedule.duration && data.calendar !== CalendarTypes.HOLIDAY) {
            schedule.duration = 1; // default duration unit is one hour
          }
          eventsCopy.push(event);
        });
        const revision = this.allStationsCalendarEvents?.[stationId]?.revision ?? 0;
        try {
          const response = await this.serviceApi.put_calendar(stationId, {
            events: eventsCopy,
            revision,
          });
          this.applyPutCalendarResponse(stationId, response as CalendarObject);
        } catch (err: unknown) {
          if (isApiError(err) && err.status === 409) {
            throw new CalendarRevisionConflictError();
          }
          throw err;
        }
      },
      async deleteCalendarEvent(stationId: string, eventToDelete?: CalendarEvent) {
        const eventToRemove = eventToDelete || this.currentEvent;
        if (!eventToRemove?.data?.id) {
          throw new Error('No event to delete or event has no ID');
        }

        let allEvents: CalendarEvent[] = [];

        if (this.structuredStationCalendarEvents?.[stationId]) {
          const { menuEvents, businessEvents, closureEvents, deliveryEvents } =
            this.structuredStationCalendarEvents[stationId];
          allEvents = [...businessEvents, ...closureEvents, ...deliveryEvents];
          Object.values(menuEvents).forEach((mEvent) => {
            allEvents = [...allEvents, ...mEvent];
          });
        }

        // Filter out the event to delete
        const eventsCopy = allEvents.filter((event) => event.data?.id !== eventToRemove.data.id);

        const revision = this.allStationsCalendarEvents?.[stationId]?.revision ?? 0;
        try {
          const response = await this.serviceApi.put_calendar(stationId, {
            events: eventsCopy,
            revision,
          });
          this.applyPutCalendarResponse(stationId, response as CalendarObject);
          this.currentEvent = null;
        } catch (err: unknown) {
          if (isApiError(err) && err.status === 409) {
            throw new CalendarRevisionConflictError();
          }
          throw err;
        }
      },
    },
    getters: {
      stationTimezone:
        (state: State) =>
        (stationStoreId?: string): string => {
          const stationStore = !isEmpty(stationStoreId)
            ? useStationStore(stationStoreId)
            : useStationStore();
          const { stationBrand } = storeToRefs(stationStore);

          const siteStore = useSiteStore();
          const { site } = storeToRefs(siteStore);

          const brandWithTimezone = site.value?.locations?.find(
            (location) => location.brands?.[0]?.timezone,
          );
          const timezone = brandWithTimezone?.brands?.[0]?.timezone ?? '';

          return (
            state.allStationsCalendarEvents?.[0]?.timezone ||
            stationBrand.value?.timezone ||
            timezone ||
            'UTC'
          );
        },
      isNewEvent: (state: State): boolean => !state.currentEvent?.data?.id,
    },
    pagination: false,
  });

  return store();
}

export {
  CalendarRevisionConflictError,
  isCalendarRevisionConflictError,
} from './calendar-revision-conflict';
